import { createContext, ReactNode, useContext, useRef, useState } from 'react';
import {
  ConfirmDiscard,
  createExclusiveEdit,
  ExclusiveEdit
} from './exclusiveEdit';

const ExclusiveEditContext = createContext<ExclusiveEdit | null>(null);

/**
 * One session for the host's lifetime. The host keeps it to read
 * `hasUnsavedDraft` (tab close, navigation) and hands it to the editors
 * through `ExclusiveEditProvider`.
 */
export function useCreateExclusiveEdit(
  confirmDiscard: ConfirmDiscard
): ExclusiveEdit {
  const confirmRef = useRef(confirmDiscard);
  confirmRef.current = confirmDiscard;
  const [exclusiveEdit] = useState(() =>
    createExclusiveEdit(draft => confirmRef.current(draft))
  );
  return exclusiveEdit;
}

export function ExclusiveEditProvider({
  value,
  children
}: {
  value: ExclusiveEdit;
  children: ReactNode;
}) {
  return (
    <ExclusiveEditContext.Provider value={value}>
      {children}
    </ExclusiveEditContext.Provider>
  );
}

export function useExclusiveEdit(): ExclusiveEdit {
  const exclusiveEdit = useContext(ExclusiveEditContext);
  if (!exclusiveEdit) {
    throw new Error(
      'useExclusiveEdit must be used within an ExclusiveEditProvider'
    );
  }
  return exclusiveEdit;
}
